import { FieldError, CollapseContentSection } from "@impero-com/ui";
import { Select } from "antd";
import { Control, useController, useFieldArray } from "react-hook-form";

import { RiskDirectoryBlessedFieldIds } from "api/generated/model";

import { HeaderButtons } from "./components/headerButtons";
import { newDefaultSection } from "./formData";
import { FormSchema } from "./schema";
import { SectionKey, SectionLevel, StructureSection } from "./sections";
import * as theme from "./theme.css";

interface StructureFormProps {
    control: Control<FormSchema>;
    blessedFieldIds: RiskDirectoryBlessedFieldIds;
}

export function StructureForm(props: StructureFormProps) {
    const { control, blessedFieldIds } = props;
    const { fields, append, remove, move } = useFieldArray({
        control,
        name: "sections",
    });

    const handleAddSection = (value: SectionLevel) => {
        append(newDefaultSection(value));
    };

    return (
        <>
            {fields.map((field, index) => (
                <CollapseContentSection
                    key={field.id}
                    level={4}
                    titleLabel={`Section ${index + 1} - ${
                        field.level
                    } level - ${field.sectionId}`}
                    styleVariant="primary"
                    initialCollapsed={true}
                    headerRightChildren={
                        <div className={theme.sectionHeaderRight}>
                            <SectionValidationError
                                control={control}
                                sectionKey={`sections.${index}`}
                            />
                            <HeaderButtons
                                onClickRemove={(index) => {
                                    remove(index);
                                }}
                                onChangeOrder={(index, value) => {
                                    move(index, value);
                                }}
                                index={index}
                                siblingsLength={fields.length}
                            />
                        </div>
                    }
                >
                    <StructureSection
                        control={control}
                        sectionKey={`sections.${index}`}
                        blessedFieldIds={blessedFieldIds}
                    />
                </CollapseContentSection>
            ))}
            <Select
                placeholder="+ Add section"
                options={[
                    {
                        label: "Risk directory level",
                        value: "RiskDirectory" satisfies SectionLevel,
                    },
                    {
                        label: "Entity level",
                        value: "Entity" satisfies SectionLevel,
                    },
                ]}
                onChange={handleAddSection}
                value={null}
            />
        </>
    );
}

interface SectionValidationErrorProps {
    control: Control<FormSchema>;
    sectionKey: SectionKey;
}

function SectionValidationError({
    control,
    sectionKey,
}: SectionValidationErrorProps) {
    const { fieldState } = useController({
        name: `${sectionKey}.level`,
        control,
    });
    return (
        <FieldError
            error={fieldState.error?.message ?? fieldState.error?.root?.message}
        />
    );
}
